
import { useCallback, useEffect, useRef, useState } from 'react';
import { ZoomIn, ZoomOut } from 'lucide-react';
import { toast } from 'sonner';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/shared/ui';
import {
  clampCrop,
  initialCrop,
  MAX_ZOOM,
  MIN_ZOOM,
  sourceRect,
  zoomAt,
  type Crop,
  type Size,
} from './avatar-crop';
import styles from './AvatarCropDialog.module.css';

const OUTPUT_PX = 512;
const KEY_STEP_PX = 12;
const KEY_ZOOM_STEP = 0.15;

async function renderCrop(img: HTMLImageElement, natural: Size, view: number, crop: Crop): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = OUTPUT_PX;
  canvas.height = OUTPUT_PX;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Браузер не дал нарисовать кадр');
  ctx.imageSmoothingQuality = 'high';
  const { sx, sy, size } = sourceRect(natural, view, crop);
  ctx.drawImage(img, sx, sy, size, size, 0, 0, OUTPUT_PX, OUTPUT_PX);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/webp', 0.9));
  if (!blob) throw new Error('Не удалось подготовить фото');
  return blob;
}

export interface AvatarCropDialogProps {
  file: File | null;
  onClose: () => void;
  onSave: (blob: Blob) => Promise<void>;
}

export function AvatarCropDialog({ file, onClose, onSave }: AvatarCropDialogProps) {
  const [src, setSrc] = useState<string | null>(null);
  const [natural, setNatural] = useState<Size | null>(null);
  const [view, setView] = useState(0);
  const [crop, setCrop] = useState<Crop>({ zoom: 1, x: 0, y: 0 });
  const [saving, setSaving] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const saveRef = useRef<HTMLButtonElement | null>(null);
  const drag = useRef<{ id: number; x: number; y: number; crop: Crop } | null>(null);
  const pinch = useRef<Map<number, { x: number; y: number }>>(new Map());
  const pinchStart = useRef<{ dist: number; zoom: number } | null>(null);

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setSrc(url);
    setNatural(null);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const stageRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return;
    setView(node.clientWidth);
  }, []);

  useEffect(() => {
    if (natural && view) setCrop(initialCrop(natural, view));
  }, [natural, view]);

  const ready = Boolean(natural && view);

  const update = (next: Crop) => {
    if (natural && view) setCrop(clampCrop(natural, view, next));
  };

  const pointOf = (e: { clientX: number; clientY: number }, el: Element) => {
    const r = el.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!ready) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    pinch.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pinch.current.size === 2) {
      const [a, b] = [...pinch.current.values()];
      pinchStart.current = { dist: Math.hypot(a.x - b.x, a.y - b.y), zoom: crop.zoom };
      drag.current = null;
      return;
    }
    drag.current = { id: e.pointerId, x: e.clientX, y: e.clientY, crop };
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!natural || !view) return;
    if (pinch.current.has(e.pointerId)) pinch.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pinch.current.size === 2 && pinchStart.current) {
      const [a, b] = [...pinch.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const mid = pointOf({ clientX: (a.x + b.x) / 2, clientY: (a.y + b.y) / 2 }, e.currentTarget);
      setCrop((c) => zoomAt(natural, view, c, pinchStart.current!.zoom * (dist / pinchStart.current!.dist), mid));
      return;
    }
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    update({ ...d.crop, x: d.crop.x + e.clientX - d.x, y: d.crop.y + e.clientY - d.y });
  };

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    pinch.current.delete(e.pointerId);
    if (pinch.current.size < 2) pinchStart.current = null;
    if (drag.current?.id === e.pointerId) drag.current = null;
  };

  const wheelTarget = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = wheelTarget.current;
    if (!el || !natural || !view) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const anchor = pointOf(e, el);
      setCrop((c) => zoomAt(natural, view, c, c.zoom * Math.exp(-e.deltaY * 0.002), anchor));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [natural, view]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!natural || !view) return;
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [KEY_STEP_PX, 0],
      ArrowRight: [-KEY_STEP_PX, 0],
      ArrowUp: [0, KEY_STEP_PX],
      ArrowDown: [0, -KEY_STEP_PX],
    };
    const move = moves[e.key];
    if (move) {
      e.preventDefault();
      update({ ...crop, x: crop.x + move[0], y: crop.y + move[1] });
    } else if (e.key === '+' || e.key === '=') {
      e.preventDefault();
      setCrop((c) => zoomAt(natural, view, c, c.zoom + KEY_ZOOM_STEP));
    } else if (e.key === '-') {
      e.preventDefault();
      setCrop((c) => zoomAt(natural, view, c, c.zoom - KEY_ZOOM_STEP));
    }
  };

  const setZoom = (zoom: number) => {
    if (natural && view) setCrop((c) => zoomAt(natural, view, c, zoom));
  };

  const save = async () => {
    const img = imgRef.current;
    if (!img || !natural || !view) return;
    setSaving(true);
    try {
      await onSave(await renderCrop(img, natural, view, crop));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Не удалось сохранить фото');
    } finally {
      setSaving(false);
    }
  };

  const scale = natural && view ? (view / Math.min(natural.w, natural.h)) * crop.zoom : 1;

  return (
    <Dialog open={file != null} onOpenChange={(open) => !open && !saving && onClose()}>
      { }
      <DialogContent className={styles.dialog} initialFocus={saveRef}>
        <DialogTitle>Фото профиля</DialogTitle>
        <DialogDescription>
          Перетащите фото, чтобы выбрать кадр. Приблизить — колесом, щипком или ползунком.
        </DialogDescription>

        <div
          ref={(node) => {
            stageRef(node);
            wheelTarget.current = node;
          }}
          className={styles.stage}
          tabIndex={0}
          role="application"
          aria-label="Кадр фото: стрелки двигают, плюс и минус приближают"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onKeyDown={onKeyDown}
          data-ready={ready || undefined}
        >
          {src && (
            <img
              ref={imgRef}
              src={src}
              alt=""
              draggable={false}
              className={styles.image}
              onLoad={(e) =>
                setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })
              }
              onError={() => {
                toast.error('Не удалось открыть файл — выберите фото в JPEG, PNG или WebP');
                onClose();
              }}
              style={
                natural
                  ? {
                      width: natural.w * scale,
                      height: natural.h * scale,
                      transform: `translate(${crop.x}px, ${crop.y}px)`,
                    }
                  : undefined
              }
            />
          )}
          <span className={styles.mask} aria-hidden />
        </div>

        <div className={styles.zoomRow}>
          <button
            type="button"
            className={styles.zoomBtn}
            aria-label="Отдалить"
            onClick={() => setZoom(crop.zoom - KEY_ZOOM_STEP * 2)}
            disabled={!ready || crop.zoom <= MIN_ZOOM}
          >
            <ZoomOut size={16} aria-hidden />
          </button>
          <input
            type="range"
            className={styles.zoomRange}
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={0.01}
            value={crop.zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            disabled={!ready}
            aria-label="Масштаб"
            style={{ '--fill': `${((crop.zoom - MIN_ZOOM) / (MAX_ZOOM - MIN_ZOOM)) * 100}%` } as React.CSSProperties}
          />
          <button
            type="button"
            className={styles.zoomBtn}
            aria-label="Приблизить"
            onClick={() => setZoom(crop.zoom + KEY_ZOOM_STEP * 2)}
            disabled={!ready || crop.zoom >= MAX_ZOOM}
          >
            <ZoomIn size={16} aria-hidden />
          </button>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Отмена
          </Button>
          <Button ref={saveRef} onClick={() => void save()} disabled={!ready || saving}>
            {saving ? 'Сохраняем…' : 'Сохранить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
